import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faStickyNote, 
  faTags, 
  faFilter, 
  faTimes, 
  faUser, 
  faCalendarAlt,
  faLightbulb,
  faSpinner,
  faEdit,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';
import AddNotePage from '../../AddNotePage/AddNotePage';
import EditNotePage from '../../EditNotePage/EditNotePage';
import ConfirmationModal from '@frontend/Components/ConfirmationModal/ConfirmationModal';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import EmployeeService from '@frontend/AppSettings/services/EmployeeService';
import toastService from '@frontend/AppSettings/services/ToastService';
import { IClientNote } from '@frontend/Models/Notes/NotesModels';
import { deleteStakeholderNote } from '../../utils/orbitStakeholderNotes';

interface NotesTagsCardProps {
  stakeholderId: string;
  type: 'individual' | 'organization';
  stakeholderData?: any;
}

// Tag color palette - consistent colors for tags
const TAG_COLORS = [
  { bg: '#dbeafe', text: '#1e40af', name: 'blue' },
  { bg: '#fce7f3', text: '#be185d', name: 'pink' },
  { bg: '#fed7aa', text: '#9a3412', name: 'orange' },
  { bg: '#dcfce7', text: '#166534', name: 'green' },
  { bg: '#e9d5ff', text: '#6b21a8', name: 'purple' },
  { bg: '#fef3c7', text: '#92400e', name: 'yellow' },
  { bg: '#fecaca', text: '#991b1b', name: 'red' },
  { bg: '#cffafe', text: '#0e7490', name: 'cyan' },
];

// Generate consistent color for a tag based on its name
const getTagColor = (tagName: string): { bg: string; text: string; name: string } => {
  // Simple hash function to consistently map tag names to colors
  let hash = 0;
  for (let i = 0; i < tagName.length; i++) {
    hash = tagName.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % TAG_COLORS.length;
  return TAG_COLORS[index];
};

// ----- Helper functions (separate concerns) -----
const parseNotesField = (notesField: any): any[] => {
  if (!notesField) return [];
  try {
    const notesData = typeof notesField === 'string' ? JSON.parse(notesField) : notesField;
    if (Array.isArray(notesData)) return notesData;
    if (typeof notesData === 'object' && notesData !== null && !Array.isArray(notesData) && Array.isArray(notesData.notes)) {
      return notesData.notes;
    }
    if (typeof notesData === 'object' && notesData !== null) return [notesData];
    return [];
  } catch (error) {
    console.error('Error parsing notes:', error);
    return [];
  }
};

const normalizeNote = (note: any, idx: number) => ({
  noteid: note.noteid != null && note.noteid !== '' ? Number(note.noteid) : idx + 1,
  note_text: note.note_text || note.noteText || '',
  tags: Array.isArray(note.tags) ? note.tags : [],
  created_at: note.created_at || note.createdAt || '',
  created_by: note.created_by || note.createdBy || '',
});

const computeTagsAndCounts = (parsedNotes: any[]) => {
  const tagsSet = new Set<string>();
  const counts = new Map<string, number>();
  parsedNotes.forEach((note: any) => {
    if (note.tags && Array.isArray(note.tags)) {
      note.tags.forEach((tag: string) => {
        tagsSet.add(tag);
        counts.set(tag, (counts.get(tag) || 0) + 1);
      });
    }
  });
  return { tagsSet, counts };
};

// Fetch employee names for created_by IDs and set state
const fetchAndSetEmployeeNames = async (
  parsedNotes: any[],
  setEmployeeNames: React.Dispatch<React.SetStateAction<Map<string, string>>>
) => {
  if (!parsedNotes || parsedNotes.length === 0) return;
  const employeeIds = Array.from(new Set(parsedNotes.map((n: any) => n.created_by).filter(Boolean)));
  if (employeeIds.length === 0) return;
  try {
    const employees = await EmployeeService.getEmployees();
    const namesMap = new Map<string, string>();
    employees.forEach((emp: any) => {
      if (employeeIds.includes(emp.EmployeeID)) {
        namesMap.set(emp.EmployeeID, `${emp.FirstName || ''} ${emp.LastName || ''}`.trim());
      }
    });
    setEmployeeNames(namesMap);
  } catch (error) {
    console.error('Error fetching employee names:', error);
  }
};

const NotesTagsCard: React.FC<NotesTagsCardProps> = ({ 
  stakeholderId, 
  type,
  stakeholderData 
}) => {
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<IClientNote | null>(null);
  const [deletingNoteId, setDeletingNoteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [notes, setNotes] = useState<IClientNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [allTags, setAllTags] = useState<Set<string>>(new Set());
  const [employeeNames, setEmployeeNames] = useState<Map<string, string>>(new Map());
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [tagCounts, setTagCounts] = useState<Map<string, number>>(new Map());
  const [showAllNotes, setShowAllNotes] = useState(false);

  // Extract actual client ID (handle org- prefix)
  const actualClientId = useMemo(() => {
    if (stakeholderId.startsWith('org-')) {
      return stakeholderId.replace('org-', '');
    }
    return stakeholderId;
  }, [stakeholderId]);

  const refreshNotes = useCallback(async () => {
    try {
      setLoading(true);
      let clientData: any = null;
      let agentData: any = null;
      try {
        clientData = await ClientService.getClientById(actualClientId);
      } catch {
        clientData = null;
      }
      if (!clientData) {
        try {
          agentData = await AgentsService.getAgent(actualClientId);
        } catch {
          agentData = null;
        }
      }

      const data = clientData || agentData;
      if (!data) {
        setNotes([]);
        setAllTags(new Set());
        setTagCounts(new Map());
        setEmployeeNames(new Map());
        return;
      }

      const notesField = (data as any).Notes ?? (data as any).notes;
      let parsedNotes = parseNotesField(notesField);
      parsedNotes = parsedNotes.map((note: any, idx: number) => normalizeNote(note, idx));

      setNotes(parsedNotes);
      const { tagsSet, counts } = computeTagsAndCounts(parsedNotes);
      setAllTags(tagsSet);
      setTagCounts(counts);
      await fetchAndSetEmployeeNames(parsedNotes, setEmployeeNames);
    } catch (error) {
      console.error('Error fetching notes:', error);
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [actualClientId]);

  useEffect(() => {
    refreshNotes();
  }, [refreshNotes, stakeholderData]);

  const handleAddNote = () => {
    setIsAddNoteModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddNoteModalOpen(false);
  };

  const handleNoteAdded = () => {
    refreshNotes();
  };

  const handleEditNote = (note: IClientNote) => {
    setEditingNote(note);
  };

  const handleCloseEditModal = () => {
    setEditingNote(null);
  };

  const handleNoteUpdated = () => {
    refreshNotes();
  };

  const handleRequestDelete = (noteId: number) => {
    setDeletingNoteId(noteId);
  };

  const handleCancelDelete = () => {
    if (isDeleting) return;
    setDeletingNoteId(null);
  };

  const handleConfirmDelete = async () => {
    if (deletingNoteId == null || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteStakeholderNote(actualClientId, deletingNoteId);
      toastService.success('Note deleted successfully');
      setDeletingNoteId(null);
      if (editingNote?.noteid === deletingNoteId) {
        setEditingNote(null);
      }
      await refreshNotes();
    } catch (error: any) {
      console.error('Error deleting note:', error);
      toastService.error(error.message || 'Failed to delete note. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Filter notes by selected tag
  const filteredNotes = useMemo(() => {
    if (!selectedTag) return notes;
    return notes.filter(note => note.tags && note.tags.includes(selectedTag));
  }, [notes, selectedTag]);

  // Collapse "show all" when filter changes so the count makes sense
  useEffect(() => {
    setShowAllNotes(false);
  }, [selectedTag]);

  // Handle tag click to filter
  const handleTagClick = (tag: string) => {
    if (selectedTag === tag) {
      setSelectedTag(null); // Toggle off if already selected
    } else {
      setSelectedTag(tag);
    }
  };

  // Clear filter
  const handleClearFilter = () => {
    setSelectedTag(null);
  };

  // How many notes to show (5 when collapsed, all when expanded)
  const notesToShow = 5;
  const displayedNotes = showAllNotes ? filteredNotes : filteredNotes.slice(0, notesToShow);
  const hasMoreNotes = filteredNotes.length > notesToShow;

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return 'N/A';
    }
  };

  return (
    <>
      <div className="stakeholder-details-card notes-tags-card">
        {/* Card Header - Original Style */}
        <div className="stakeholder-card-header">
          <div className="stakeholder-card-header-left">
            <FontAwesomeIcon icon={faStickyNote} className="stakeholder-card-icon" />
            <h3 className="stakeholder-card-title">Notes & Tags</h3>
          </div>
          <button className="stakeholder-add-button" onClick={handleAddNote}>
            <FontAwesomeIcon icon={faPlus} />
            Add Note
          </button>
        </div>

        <div className="stakeholder-card-content notes-card-content">
          {loading ? (
            <div className="notes-loading-state">
              <FontAwesomeIcon icon={faSpinner} spin className="notes-loading-spinner" />
              <span>Loading notes...</span>
            </div>
          ) : (
            <>
              {/* Tags Section - Clickable filters */}
              {allTags.size > 0 && (
                <div className="notes-tags-section">
                  <div className="notes-section-header">
                    <FontAwesomeIcon icon={faTags} className="notes-section-icon" />
                    <span className="notes-section-label">Filter by Tag</span>
                    {selectedTag && (
                      <button 
                        className="notes-clear-filter-btn"
                        onClick={handleClearFilter}
                        title="Clear filter"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>Clear</span>
                      </button>
                    )}
                  </div>
                  <div className="notes-tags-list">
                    {Array.from(allTags).map((tag) => {
                      const color = getTagColor(tag);
                      const count = tagCounts.get(tag) || 0;
                      const isActive = selectedTag === tag;
                      return (
                        <button
                          key={tag}
                          className={`notes-tag-pill ${isActive ? 'notes-tag-pill-active' : ''}`}
                          style={{
                            '--tag-bg': color.bg,
                            '--tag-text': color.text,
                            '--tag-active-bg': color.text,
                          } as React.CSSProperties}
                          onClick={() => handleTagClick(tag)}
                          title={`Filter by "${tag}" - ${count} note${count !== 1 ? 's' : ''}`}
                        >
                          <span className="notes-tag-name">{tag}</span>
                          <span className="notes-tag-badge">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Active Filter Banner */}
              {selectedTag && filteredNotes.length > 0 && (
                <div className="notes-active-filter">
                  <FontAwesomeIcon icon={faFilter} className="notes-filter-icon" />
                  <span>
                    Showing <strong>{filteredNotes.length}</strong> note{filteredNotes.length !== 1 ? 's' : ''} tagged 
                    <span className="notes-filter-tag-name">"{selectedTag}"</span>
                  </span>
                </div>
              )}

              {/* Notes Section */}
              {notes.length === 0 ? (
                <div className="notes-empty-state">
                  <div className="notes-empty-icon-wrapper">
                    <FontAwesomeIcon icon={faLightbulb} className="notes-empty-icon" />
                  </div>
                  <h4 className="notes-empty-title">No notes yet</h4>
                  <p className="notes-empty-text">
                    Add notes to track important information about this {type === 'organization' ? 'organization' : 'stakeholder'}.
                  </p>
                  <button className="notes-empty-action" onClick={handleAddNote}>
                    <FontAwesomeIcon icon={faPlus} />
                    Add your first note
                  </button>
                </div>
              ) : filteredNotes.length === 0 ? (
                <div className="notes-empty-state notes-empty-filtered">
                  <div className="notes-empty-icon-wrapper notes-empty-icon-filtered">
                    <FontAwesomeIcon icon={faFilter} className="notes-empty-icon" />
                  </div>
                  <h4 className="notes-empty-title">No matching notes</h4>
                  <p className="notes-empty-text">
                    No notes found with the tag "{selectedTag}".
                  </p>
                  <button className="notes-empty-action notes-empty-action-secondary" onClick={handleClearFilter}>
                    Show all notes
                  </button>
                </div>
              ) : (
                <div className="notes-list">
                  {displayedNotes.map((note, index) => {
                    const createdByName = employeeNames.get(note.created_by || '') || 'Unknown';
                    const createdDate = formatDate(note.created_at || '');
                    return (
                      <div 
                        key={note.noteid} 
                        className="note-card"
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <div className="note-card-content">
                          <p className="note-card-text">{note.note_text || ''}</p>
                          
                          {note.tags && note.tags.length > 0 && (
                            <div className="note-card-tags">
                              {note.tags.map((tag) => {
                                const color = getTagColor(tag);
                                const isHighlighted = tag === selectedTag;
                                return (
                                  <span
                                    key={tag}
                                    className={`note-card-tag ${isHighlighted ? 'note-card-tag-active' : ''}`}
                                    style={{
                                      backgroundColor: isHighlighted ? color.text : color.bg,
                                      color: isHighlighted ? '#ffffff' : color.text,
                                    }}
                                  >
                                    {tag}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        
                        <div className="note-card-footer">
                          <div className="note-card-meta">
                            <span className="note-card-author">
                              <FontAwesomeIcon icon={faUser} />
                              {createdByName}
                            </span>
                            <span className="note-card-divider">·</span>
                            <span className="note-card-date">
                              <FontAwesomeIcon icon={faCalendarAlt} />
                              {createdDate}
                            </span>
                          </div>
                          <div className="note-card-actions">
                            <button
                              type="button"
                              className="note-card-action-btn note-card-edit-btn"
                              aria-label="Edit note"
                              title="Edit note"
                              onClick={() => handleEditNote(note)}
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button
                              type="button"
                              className="note-card-action-btn note-card-delete-btn"
                              aria-label="Delete note"
                              title="Delete note"
                              onClick={() => handleRequestDelete(note.noteid)}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {hasMoreNotes && (
                    <button
                      type="button"
                      className="notes-show-more notes-show-more-btn"
                      onClick={() => setShowAllNotes(!showAllNotes)}
                      aria-expanded={showAllNotes}
                      aria-label={showAllNotes ? 'Show fewer notes' : `Show ${filteredNotes.length - notesToShow} more notes`}
                    >
                      <span className="notes-show-more-text">
                        {showAllNotes
                          ? 'Show less'
                          : `+${filteredNotes.length - notesToShow} more note${filteredNotes.length - notesToShow !== 1 ? 's' : ''}`}
                      </span>
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      
      {isAddNoteModalOpen && (
        <AddNotePage
          onClose={handleCloseModal}
          stakeholderId={actualClientId}
          type={type}
          stakeholderData={stakeholderData}
          onNoteAdded={handleNoteAdded}
          existingTags={Array.from(allTags)}
        />
      )}

      {editingNote && (
        <EditNotePage
          onClose={handleCloseEditModal}
          stakeholderId={actualClientId}
          type={type}
          note={editingNote}
          onNoteUpdated={handleNoteUpdated}
          existingTags={Array.from(allTags)}
        />
      )}

      <ConfirmationModal
        isOpen={deletingNoteId != null}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        message="Are you sure you want to delete this note? This action cannot be undone."
        confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
        cancelLabel="Cancel"
      />
    </>
  );
};

export default NotesTagsCard;

